import { IPaginatedRepository, IRepository } from '@/repositories/types'
import { useToast } from '@chakra-ui/react'
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query'

/**
 * @file Defines a generic hook for handling Create, Update, and Delete (CUD) operations.
 * @summary This hook abstracts CUD logic, providing a consistent and reusable way to
 * perform mutations with any repository that implements the IRepository interface.
 */

export interface CRUDOptions<T, TCreate, TUpdate, TError> {
  /** A unique key for the entity, used for query invalidation. */
  queryKey: (string | number)[]
  /** The repository instance to use for the CUD operations. */
  repository:
    | IRepository<T, TCreate, TUpdate>
    | IPaginatedRepository<T, TCreate, TUpdate>
  /** Optional custom messages for toast notifications. */
  messages?: {
    createSuccess?: string
    createError?: string
    updateSuccess?: string
    updateError?: string
    deleteSuccess?: string
    deleteError?: string
    restoreSuccess?: string
    restoreError?: string
  }
  /** Optional callbacks for mutation events. */
  onSuccess?: (
    operation: 'create' | 'update' | 'delete' | 'restore',
    data?: T | void
  ) => void
  onError?: (
    operation: 'create' | 'update' | 'delete' | 'restore',
    error: TError
  ) => void
}

/**
 * A generic hook for performing CUD (Create, Update, Delete) operations.
 *
 * @template T - The type of the entity.
 * @template TCreate - The type for creating a new entity.
 * @template TUpdate - The type for updating an entity.
 * @template TError - The type of the error that might be thrown.
 */
export function useCRUD<T, TCreate, TUpdate, TError extends Error = Error>({
  queryKey,
  repository,
  messages,
  onSuccess,
  onError,
}: CRUDOptions<T, TCreate, TUpdate, TError>) {
  const queryClient = useQueryClient()
  const toast = useToast()

  const defaultMessages = {
    createSuccess: 'Item criado com sucesso.',
    createError: 'Erro ao criar item.',
    updateSuccess: 'Item atualizado com sucesso.',
    updateError: 'Erro ao atualizar item.',
    deleteSuccess: 'Item excluído com sucesso.',
    deleteError: 'Erro ao excluir item.',
    restoreSuccess: 'Item restaurado com sucesso.',
    restoreError: 'Erro ao restaurar item.',
  }

  const finalMessages = { ...defaultMessages, ...messages }

  const useMutationWrapper = <TData extends T | void, TVariables>(
    operation: 'create' | 'update' | 'delete' | 'restore',
    mutationFn: (variables: TVariables) => Promise<TData>,
    options?: UseMutationOptions<TData, TError, TVariables>
  ) => {
    return useMutation<TData, TError, TVariables>({
      mutationFn,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey })

        if (
          (operation === 'update' ||
            operation === 'delete' ||
            operation === 'restore') &&
          variables &&
          typeof variables === 'object' &&
          'id' in variables &&
          variables.id
        ) {
          const singleItemQueryKey = [queryKey[0], variables.id]

          queryClient.invalidateQueries({
            queryKey: singleItemQueryKey,
          })
        }

        if (onSuccess) {
          return onSuccess?.(operation, data)
        }
        toast({
          title: finalMessages[`${operation}Success`],
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      },
      onError: (error) => {
        toast({
          title: finalMessages[`${operation}Error`],
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        onError?.(operation, error)
      },
      ...options,
    })
  }

  const createMutation = useMutationWrapper('create', (data: TCreate) => {
    if (!repository.create) {
      throw new Error('A função de criar não é suportada por este repositório.')
    }

    return repository.create(data)
  })

  const updateMutation = useMutationWrapper(
    'update',
    ({ id, data }: { id: string | number; data: TUpdate }) => {
      if (!repository.update) {
        throw new Error(
          'A função de atualizar não é suportada por este repositório.'
        )
      }
      return repository.update(id, data)
    }
  )

  const deleteMutation = useMutationWrapper('delete', (id: string | number) => {
    if (!repository.delete) {
      throw new Error(
        'A função de excluir não é suportada por este repositório.'
      )
    }
    return repository.delete(id)
  })

  const restoreMutation = useMutationWrapper(
    'restore',
    (id: string | number) => {
      if (!repository.restore) {
        throw new Error(
          'A função de restaurar não é suportada por este repositório.'
        )
      }
      return repository.restore(id)
    }
  )

  return {
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,

    update: updateMutation.mutate,
    updateAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,

    delete: deleteMutation.mutate,
    deleteAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,

    restore: restoreMutation.mutate,
    restoreAsync: restoreMutation.mutateAsync,
    isRestoring: restoreMutation.isPending,
  }
}
